/**
 * GQty: You can safely modify this file based on your needs.
 */

import {
  Cache,
  createClient,
  defaultResponseHandler,
  GQtyError,
  type QueryFetcher
} from 'gqty';
import { DataFn, ResolveOptions } from 'gqty/Client/resolvers';
import { createClient as createSubscriptionsClient } from 'graphql-ws';
import {
  generatedSchema,
  scalarsEnumsHash,
  type GeneratedSchema,
} from './schema.generated';

const rawSchema = generatedSchema as Record<string, Record<string, { __type: string }>>;
for (const [typeName, typeDef] of Object.entries(rawSchema)) {
  for (const [propName, propDef] of Object.entries(typeDef)) {
    if (propDef.__type === '[[Position]]') {
      rawSchema[typeName][propName].__type = '[Position]';
    }
  }
}

const queryFetcher: QueryFetcher = async function (
  { query, variables, operationName },
  fetchOptions
) {
  // Modify "/api/graphql/" if needed
  const response = await fetch('/api/graphql/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query,
      variables,
      operationName,
    }),
    mode: 'cors',
    ...fetchOptions,
  });

  return await defaultResponseHandler(response);
};

const subscriptionsClient =
  typeof window !== 'undefined'
    ? createSubscriptionsClient({
        lazy: true,
        url: () => {
          // Modify if needed
          const url = new URL(
            '/api/graphql/',
            window.location.href
          );
          url.protocol = url.protocol.replace('http', 'ws');
          return url.href;
        },
      })
    : undefined;

const cache = new Cache(
  undefined,
  /**
   * Default option is immediate cache expiry but keep it for 5 minutes,
   * allowing soft refetches in background.
   */
  {
    maxAge: 0,
    staleWhileRevalidate: 5 * 60 * 1000,
    normalization: true,
  }
);

export const client = createClient<GeneratedSchema>({
  schema: generatedSchema,
  scalars: scalarsEnumsHash,
  cache,
  fetchOptions: {
    fetcher: queryFetcher,
    subscriber: subscriptionsClient,
  },
});

// Core functions
export const { subscribe, schema } = client;

/**
 * Options accepted by the module-level {@link resolve} wrapper.
 *
 * On top of GQty's {@link ResolveOptions}, an `AbortSignal` can be provided so
 * the underlying `fetch` can be cancelled (e.g. when TanStack Query aborts a
 * stale query while the user keeps zooming/panning the map).
 */
export type ResolveWithSignalOptions = ResolveOptions & {
  signal?: AbortSignal;
};

export const resolve = <TData = unknown>(
  fn: DataFn<GeneratedSchema, TData>,
  options?: ResolveWithSignalOptions
): Promise<TData> => {
  const { signal, ...resolveOptions } = options ?? {};

  const newQueryFetcher: QueryFetcher = async function (
    { query, variables, operationName },
    fetchOptions
  ) {
    try {
      // Modify "/api/graphql/" if needed
      const response = await fetch('/api/graphql/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables,
          operationName,
        }),
        mode: 'cors',
        ...fetchOptions,
        signal,
      });

      return await defaultResponseHandler(response);
    } catch (error) {
      // When the request is aborted, surface a GQtyError so GQty treats it as
      // terminating and does NOT retry it (its default retryPolicy would retry
      // any plain Error up to 3 times, keeping the request alive).
      //
      if ((error as { name?: string } | null)?.name === 'AbortError') {
        throw new GQtyError('Request aborted');
      }
      throw error;
    }
  };

  const newCache = new Cache(
    undefined,
    /**
     * This client is transient: it is created for a single `resolve()` call and
     * discarded right after. TanStack Query is the real cache layer on top of
     * this, so this cache only needs to survive one resolution.
     *
     * - `maxAge: Infinity` keeps entries fresh for the call. With `maxAge: 0`
     *   every entry GQty writes is stale on the very next access (see
     *   `age < now` in Client/context), making GQty re-resolve/re-fetch the same
     *   selection — duplicate requests and unstable reads.
     * - `normalization: false`: normalization makes GQty `crawl()` the entire
     *   response tree (Cache/index `getId` path) with a hard 100k-node cap
     *   (Cache/crawl). Large list responses (e.g. the finest-LOD `itemsWithLod`,
     *   thousands of rows × many coordinate pairs) blow past that cap and throw
     *   "Maximum iterations reached", which surfaced as the map blanking at high
     *   zoom. These rows are keyless anyway (no `id`), so normalization brought
     *   no benefit — only the crawl cost.
     */
    {
      maxAge: Infinity,
      staleWhileRevalidate: 0,
      normalization: false,
    }
  );

  const newClient = createClient<GeneratedSchema>({
    schema: generatedSchema,
    scalars: scalarsEnumsHash,
    cache: newCache,
    fetchOptions: {
      fetcher: newQueryFetcher,
      subscriber: subscriptionsClient,
    },
  });
  return newClient.resolve(fn, resolveOptions);
};

/**
 * Curried variant of {@link resolve} that binds the resolve options up-front.
 *
 * This keeps the actual `resolve` call single-argument at the call site
 * (`resolveWith(options)(({ query }) => ...)`), so wiring up things like
 * cancellation (`signal`) or a cache policy does not force a reformat of the
 * GraphQL selection body.
 */
export const resolveWith =
  (options?: ResolveWithSignalOptions) =>
  <TData = unknown>(fn: DataFn<GeneratedSchema, TData>): Promise<TData> =>
    resolve(fn, options);

// Legacy functions
export const {
  query,
  mutation,
  mutate,
  subscription,
  resolved,
  refetch,
  track,
} = client;

export * from './schema.generated';
